/**
 * ChatEmptyState — replaces ``<Messages>`` when the chat pane has no
 * messages yet (cold-start or post ``/clear``). 2 vertical layers stacked
 * above the sticky ``<Composer />``:
 *
 *   1. **Time-aware greeting + briefing** — "Buongiorno {firstName}."
 *      headline under the presiding brand blob, followed by AI-curated
 *      bullets from the inbox brief (max 3) + a "Vediamo tutto" CTA.
 *      Bullets/CTA drop when the brief is empty AND not loading.
 *
 *   2. **Quick prompt chips** — 4 chips per active workspace tab. The
 *      tab id is read off ``useWorkspaceStore(selectActiveTab)``; tap
 *      submits the prompt straight through ``useSendQuery`` (no
 *      composer round-trip). Lucide icons only — no emoji.
 *
 * Animations: every layer respects ``prefers-reduced-motion`` — when
 * the user has opted out we skip the framer-motion staging entirely.
 *
 * a11y:
 *   - Layer 1 bullets are wrapped in ``aria-live="polite"`` so a screen
 *     reader announces them when the brief lands.
 *   - Chips are real ``<button>`` with explicit ``aria-label``.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { cn, useReducedMotion } from "@ando/ui";
import { LineBlob } from "@ando/ui/blob";
import { motion } from "framer-motion";

import { readCachedSession } from "@/features/auth/session";
import {
  ArrowRight,
  BarChart3,
  Bot,
  Calendar,
  CheckCircle,
  Clock,
  Compass,
  Database,
  FileText,
  Layers,
  LayoutDashboard,
  ListChecks,
  type LucideIcon,
  Mail,
  MessageSquare,
  RefreshCw,
  Search,
  Send,
  Share2,
  Sparkles,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import { type FC, useMemo } from "react";

import { useInboxBrief } from "@/features/briefing/briefingApi";
import { onboardingKey, useDemoTourStore } from "@/features/onboarding";
// `handleBriefAction` / `resolveBriefHeroCta` no longer wired here —
// `BriefAggregate` (rendered inside the Work hub) handles per-brief CTAs.
// The cold-start hero only routes to `/workspace/work` as the canonical
// "see the full briefing" entry point.
import { useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/features/settings";
import { type WorkspaceTab, selectActiveTab, useWorkspaceStore } from "@/features/workspace-router";
import { getWorkspaceTabDef } from "@/features/workspace/config";
import { useNavigate } from "@tanstack/react-router";

import type { MessageDescriptor } from "@lingui/core";

import { useChatUserId } from "../hooks/useChatSession";
import { firstNameOf } from "../lib/displayName";
import { useSendQuery } from "../hooks/useSendQuery";
import type { ChatFocus } from "../state/types";

// ─── Greeting (time-aware) ────────────────────────────────────────────────

/**
 * Italian time-of-day greeting. Buckets match Andrea's daily rhythm
 * (corp / EU-CET); midnight bucket nods to a "still up?" lightness.
 */
function pickGreeting(hour: number): MessageDescriptor {
  if (hour >= 5 && hour < 12) return msg`Buongiorno`;
  if (hour >= 12 && hour < 18) return msg`Buon pomeriggio`;
  if (hour >= 18 && hour < 24) return msg`Buonasera`;
  return msg`Notte bianca?`;
}

// ``firstNameOf`` extracted to ``../lib/displayName`` (Jun 8, 2026) so
// the composer placeholder in ``routes/_authed/route.tsx`` reuses the
// same derivation — single source of truth for personalised copy.

// ─── Quick prompts ────────────────────────────────────────────────────────

export interface QuickPromptDef {
  readonly id: string;
  readonly label: MessageDescriptor;
  readonly prompt: MessageDescriptor;
  readonly icon: LucideIcon;
}

/**
 * Per-tab quick prompt grid. 4 entries each (2x2 layout); each entry
 * carries the chip label, the prompt actually sent to ``useSendQuery``,
 * and the lucide icon. No emoji — design-system constraint.
 */
export const QUICK_PROMPTS_BY_TAB: Record<WorkspaceTab, readonly QuickPromptDef[]> = {
  conversations: [
    {
      id: "conv-unread-urgent",
      label: msg`Riassumi email urgenti`,
      prompt: msg`Riassumi le email non lette urgenti.`,
      icon: Mail,
    },
    {
      id: "conv-followup",
      label: msg`Scrivi follow-up`,
      prompt: msg`Scrivi un follow-up alle email che aspettano una mia risposta.`,
      icon: Send,
    },
    {
      id: "conv-invoices",
      label: msg`Fatture in arrivo`,
      prompt: msg`Trova le fatture in arrivo nelle email di questa settimana.`,
      icon: FileText,
    },
    {
      id: "conv-archive",
      label: msg`Email da archiviare`,
      prompt: msg`Quali email posso archiviare senza rischio?`,
      icon: ListChecks,
    },
  ],
  calendar: [
    {
      id: "cal-today",
      label: msg`Cosa devo fare oggi`,
      prompt: msg`Cosa devo fare oggi? Mostrami priorità, scadenze e meeting.`,
      icon: Calendar,
    },
    {
      id: "cal-week",
      label: msg`Chi vedo questa settimana`,
      prompt: msg`Chi vedo questa settimana? Raggruppa per cliente / azienda.`,
      icon: Users,
    },
    {
      id: "cal-reschedule",
      label: msg`Riprogramma meeting`,
      prompt: msg`Quali meeting in conflitto posso riprogrammare?`,
      icon: RefreshCw,
    },
    {
      id: "cal-brief",
      label: msg`Brief per prossimo meeting`,
      prompt: msg`Prepara un brief per il prossimo meeting in agenda.`,
      icon: Sparkles,
    },
  ],
  finance: [
    {
      id: "fin-cashflow",
      label: msg`Riepilogo cashflow`,
      prompt: msg`Riepilogo cashflow del trimestre corrente.`,
      icon: Wallet,
    },
    {
      id: "fin-overdue",
      label: msg`Fatture scadute`,
      prompt: msg`Mostrami le fatture scadute da incassare.`,
      icon: Clock,
    },
    {
      id: "fin-incoming",
      label: msg`Prossimi incassi`,
      prompt: msg`Quali incassi sono attesi nei prossimi 30 giorni?`,
      icon: ArrowRight,
    },
    {
      id: "fin-expenses",
      label: msg`Spese settimana scorsa`,
      prompt: msg`Riepiloga le spese della settimana scorsa per categoria.`,
      icon: ListChecks,
    },
  ],
  sales: [
    {
      id: "sales-pipeline",
      label: msg`Stato della pipeline`,
      prompt: msg`Mostrami lo stato della pipeline: opportunità per fase e valore.`,
      icon: BarChart3,
    },
    {
      id: "sales-hot-leads",
      label: msg`Lead da contattare`,
      prompt: msg`Quali lead sono caldi e dovrei contattare oggi?`,
      icon: Users,
    },
    {
      id: "sales-followup",
      label: msg`Follow-up opportunità`,
      prompt: msg`Scrivi un follow-up alle opportunità ferme da più di una settimana.`,
      icon: Send,
    },
    {
      id: "sales-closing",
      label: msg`In chiusura questo mese`,
      prompt: msg`Quali opportunità sono previste in chiusura questo mese?`,
      icon: Clock,
    },
  ],
  documents: [
    {
      id: "doc-recent",
      label: msg`Modificati questa settimana`,
      prompt: msg`Quali documenti sono stati modificati questa settimana?`,
      icon: FileText,
    },
    {
      id: "doc-contracts",
      label: msg`Contratti firmati`,
      prompt: msg`Trova i contratti firmati negli ultimi 30 giorni.`,
      icon: CheckCircle,
    },
    {
      id: "doc-review",
      label: msg`Documenti da revisionare`,
      prompt: msg`Quali documenti aspettano la mia revisione?`,
      icon: ListChecks,
    },
    {
      id: "doc-search-client",
      label: msg`Cerca per cliente`,
      prompt: msg`Cerca documenti recenti per un cliente specifico.`,
      icon: Search,
    },
  ],
  technical: [
    {
      id: "tech-bugs",
      label: msg`Bug aperti`,
      prompt: msg`Quali bug sono aperti questa settimana?`,
      icon: Wrench,
    },
    {
      id: "tech-today",
      label: msg`Cosa ho da fare oggi`,
      prompt: msg`Cosa ho da fare oggi sui task tecnici?`,
      icon: ListChecks,
    },
    {
      id: "tech-pr",
      label: msg`PR da revisionare`,
      prompt: msg`Quali pull request aspettano la mia review?`,
      icon: CheckCircle,
    },
    {
      id: "tech-standup",
      label: msg`Note dello standup`,
      prompt: msg`Riassumi le note dell'ultimo standup tecnico.`,
      icon: MessageSquare,
    },
  ],
  work: [
    {
      id: "work-overdue",
      label: msg`Task in ritardo`,
      prompt: msg`Quali task sono in ritardo?`,
      icon: Clock,
    },
    {
      id: "work-tomorrow",
      label: msg`Cosa farò domani`,
      prompt: msg`Pianifica cosa farò domani in base alle priorità correnti.`,
      icon: ArrowRight,
    },
    {
      id: "work-achievements",
      label: msg`Achievement settimana`,
      prompt: msg`Cosa ho portato a casa questa settimana?`,
      icon: Sparkles,
    },
    {
      id: "work-blockers",
      label: msg`Blockers attuali`,
      prompt: msg`Quali sono i miei blockers attivi e come posso sbloccarli?`,
      icon: ListChecks,
    },
  ],
  dashboards: [
    {
      id: "dashboard-list",
      label: msg`Le mie dashboard`,
      prompt: msg`Mostrami tutte le mie dashboard.`,
      icon: LayoutDashboard,
    },
    {
      id: "dashboard-create",
      label: msg`Crea nuova dashboard`,
      prompt: msg`Crea una nuova dashboard.`,
      icon: Sparkles,
    },
    {
      id: "dashboard-add-chart",
      label: msg`Aggiungi un grafico`,
      prompt: msg`Aiutami ad aggiungere un grafico a una dashboard esistente.`,
      icon: BarChart3,
    },
    {
      id: "dashboard-shared",
      label: msg`Condivise con me`,
      prompt: msg`Quali dashboard hanno condiviso con me?`,
      icon: Share2,
    },
  ],
  canvases: [
    {
      id: "canvas-recent",
      label: msg`Canvas recenti`,
      prompt: msg`Mostrami i canvas su cui ho lavorato di recente.`,
      icon: Layers,
    },
    {
      id: "canvas-create",
      label: msg`Crea canvas per progetto`,
      prompt: msg`Crea un canvas per un nuovo progetto.`,
      icon: Sparkles,
    },
    {
      id: "canvas-shared",
      label: msg`Condivisi con me`,
      prompt: msg`Quali canvas hanno condiviso con me?`,
      icon: Share2,
    },
    {
      id: "canvas-templates",
      label: msg`Template canvas`,
      prompt: msg`Mostrami i template canvas disponibili.`,
      icon: ListChecks,
    },
  ],
  automations: [
    {
      id: "auto-active",
      label: msg`Automazioni attive`,
      prompt: msg`Quali automazioni sono attive in questo momento?`,
      icon: Bot,
    },
    {
      id: "auto-create",
      label: msg`Crea workflow`,
      prompt: msg`Aiutami a creare un nuovo workflow di automazione.`,
      icon: Sparkles,
    },
    {
      id: "auto-errors",
      label: msg`Errori workflow`,
      prompt: msg`Quali workflow hanno errori recenti?`,
      icon: RefreshCw,
    },
    {
      id: "auto-test",
      label: msg`Testa workflow`,
      prompt: msg`Quali workflow vale la pena testare?`,
      icon: CheckCircle,
    },
  ],
  flows: [
    {
      id: "flows-create",
      label: msg`Crea un flow`,
      prompt: msg`Aiutami a creare un flow visuale che automatizzi un processo ricorrente.`,
      icon: Sparkles,
    },
    {
      id: "flows-active",
      label: msg`Flow attivi`,
      prompt: msg`Quali flow ho attivi in questo momento?`,
      icon: Bot,
    },
    {
      id: "flows-ideas",
      label: msg`Cosa posso automatizzare`,
      prompt: msg`Suggeriscimi automazioni utili in base ai miei strumenti collegati.`,
      icon: RefreshCw,
    },
  ],
  shares: [
    {
      id: "shares-received",
      label: msg`Ricevute di recente`,
      prompt: msg`Quali conversazioni mi hanno condiviso di recente?`,
      icon: Share2,
    },
    {
      id: "shares-sent",
      label: msg`Cosa ho condiviso`,
      prompt: msg`Quali conversazioni ho condiviso negli ultimi 30 giorni?`,
      icon: Share2,
    },
  ],
  shared: [
    {
      id: "shared-recent",
      label: msg`Condivisi recenti`,
      prompt: msg`Cosa è stato condiviso con me di recente?`,
      icon: Share2,
    },
    {
      id: "shared-mine",
      label: msg`Cosa ho condiviso`,
      prompt: msg`Cosa ho condiviso negli ultimi 30 giorni?`,
      icon: Send,
    },
    {
      id: "shared-team",
      label: msg`Condivisi col team`,
      prompt: msg`Cosa hanno condiviso le persone del mio team?`,
      icon: Users,
    },
    {
      id: "shared-pending",
      label: msg`In attesa di revisione`,
      prompt: msg`Quali condivisioni aspettano una mia azione?`,
      icon: ListChecks,
    },
  ],
};

// ─── Component ────────────────────────────────────────────────────────────

export interface ChatEmptyStateProps {
  /** Optional class for the outer pane wrapper. */
  className?: string;
}

/** Animation constant — tuned to match the Ando v18 motion vocabulary. */
const ENTER_TRANSITION = { duration: 0.22, ease: [0.32, 0.72, 0, 1] as const };

export const ChatEmptyState: FC<ChatEmptyStateProps> = ({ className }) => {
  const reducedMotion = useReducedMotion();
  const { i18n } = useLingui();

  // Hour bucket — computed once per mount; the chat pane is short-lived
  // (re-mounts on every conversation switch), so no stale-greeting risk.
  const hour = useMemo(() => new Date().getHours(), []);
  const greeting = i18n._(pickGreeting(hour));

  // Profile → first name. ``useProfile`` is gated on a non-null userId so
  // a logged-in user always lands here with the session ready.
  const userId = useChatUserId();
  const profile = useProfile(userId);
  const firstName = firstNameOf(profile.data?.name);

  // Brief — drives Layer 1 bullets + the "Vediamole insieme" CTA.
  const brief = useInboxBrief("morning");
  const bullets = useMemo(() => {
    if (!brief.data || brief.data.empty_state) return [];
    return brief.data.bullets.slice(0, 3);
  }, [brief.data]);

  // Active workspace tab → chip grid + eyebrow.
  const activeTab = useWorkspaceStore(selectActiveTab);
  const tabDef = getWorkspaceTabDef(activeTab);
  // Localize via labelMsg (not the static IT ``label``) so the eyebrow follows
  // the active Lingui locale like the rest of the workspace chrome.
  const tabLabel = tabDef ? i18n._(tabDef.labelMsg) : i18n._(msg`Workspace`);
  const prompts = QUICK_PROMPTS_BY_TAB[activeTab];

  // Send pipeline — used by chip click; reused, NOT a new hook.
  const { send } = useSendQuery();
  const handlePromptClick = (prompt: MessageDescriptor): void => {
    void send({ text: i18n._(prompt) });
  };

  // Demo onboarding — shown only to the public demo's ephemeral users. The
  // prompts target the seeded Italian demo data (Rossi Forniture).
  const isDemo = Boolean(readCachedSession()?.is_demo);
  // Jul 2026 — per-chip focus (was: blanket ``focus:"full"`` on every chip).
  // The blanket "full" made even an email/RAG question spin up the enterprise
  // SQL leg — slow, and it surfaced a spurious "database query failed" note.
  // Native ``standard`` focus already covers RAG (email/docs/corpus) AND the
  // bank (saldo/movimenti live in the internal ``banking`` provider), so those
  // chips stay native + fast. ONLY the external gestionale questions (fatture,
  // clienti — postgresql text-to-SQL) opt into ``structured`` focus, where the
  // extra SQL latency is justified because the question IS about that data.
  const demoPrompts: { text: string; focus?: ChatFocus }[] = [
    { text: i18n._(msg`Riassumi le email che richiedono risposta`) },
    { text: i18n._(msg`Qual è la policy ferie aziendale?`) },
    { text: i18n._(msg`Qual è il saldo e i movimenti del mese?`) },
    { text: i18n._(msg`Quali fatture sono scadute?`), focus: "structured" },
    { text: i18n._(msg`Mostrami i 5 clienti con più fatturato`), focus: "structured" },
  ];

  // Jun 10, 2026 — "Vediamole insieme" lands on ``/workspace/work``
  // unconditionally. The Work hub renders the FULL multi-domain
  // briefing aggregate (master headline + AI overview + per-domain
  // widgets + AI-pre-compiled tasks) so the user gets every signal
  // we surfaced in the cold-start teaser instead of jumping
  // domain-by-domain. Replaces the previous per-brief-action routing
  // (Jun 8) which deep-linked into a single domain and felt narrow now
  // that the briefing covers 5 domains.
  const navigate = useNavigate();
  const handleHeroCta = (): void => {
    void navigate({ to: "/workspace/work" });
  };

  // ── Motion props (no-op when reduced motion is on) ────────────────────
  const heroMotion = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: ENTER_TRANSITION,
      };

  const chipMotion = (index: number): Record<string, unknown> => {
    if (reducedMotion) return {};
    return {
      initial: { opacity: 0, y: 6 },
      animate: { opacity: 1, y: 0 },
      transition: { ...ENTER_TRANSITION, delay: 0.05 + index * 0.03 },
    };
  };

  // ── Render gating ──────────────────────────────────────────────────────
  // Layer 1 visible while loading (skeleton) or whenever we have bullets,
  // OR when the brief is in its happy "tutto sotto controllo" empty state.
  // We always show the headline because the greeting itself has value
  // (matches the Linear "Cold start" empty pattern from May 2026 audit).
  const showBullets = brief.isLoading || bullets.length > 0;

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col gap-6 overflow-y-auto px-8 pt-[6vh] pb-4",
        className,
      )}
    >
      {/* ── Demo onboarding — only for the public demo's ephemeral users ── */}
      {isDemo ? (
        <motion.section
          {...heroMotion}
          aria-label={i18n._(msg`Benvenuto nella demo`)}
          className="rounded-2xl border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 p-5"
        >
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ando-orange/15 text-ando-orange"
            >
              <Sparkles size={15} strokeWidth={1.75} />
            </span>
            <h2 className="font-semibold text-[15px] text-ink-1">
              <Trans>Benvenuto nella demo di Ando</Trans>
            </h2>
          </div>
          <p className="mt-2 text-[13px] text-ink-2 leading-relaxed">
            <Trans>
              Questa è una demo live: stai esplorando Rossi Forniture, un'azienda
              fittizia con email, fatture, gestionale, banca e documenti già
              collegati. Ando li unisce in un solo Company Brain — chiedi in
              linguaggio naturale e trova, collega e ragiona sui dati tra i
              sistemi. I dati sono finti e le azioni simulate: esplora senza
              rischi. Tocca una domanda qui sotto per iniziare.
            </Trans>
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {demoPrompts.map((p) => (
              <button
                key={p.text}
                type="button"
                onClick={() =>
                  void send(
                    p.focus
                      ? { text: p.text, configOverride: { focus: p.focus } }
                      : { text: p.text },
                  )
                }
                className="inline-flex items-center gap-1.5 rounded-full border border-ando-ink/10 bg-card px-3.5 py-1.5 text-[12.5px] text-ink-1 transition-colors hover:border-ando-orange/40 hover:bg-ando-orange-soft/60"
              >
                {/* Data chips carry a single orange pencil-stroke cue (Database
                    glyph) — the same mark the hint below references, so the
                    "questo segno tocca il gestionale" teaching lands visually. */}
                {p.focus ? (
                  <Database
                    size={12}
                    strokeWidth={2}
                    className="shrink-0 text-ando-orange"
                    aria-hidden
                  />
                ) : null}
                {p.text}
              </button>
            ))}
          </div>
          {/* Teach-by-showing hint — typography-first, no card, one accent.
              Anchors the orange Database mark on the data chips to the action
              (open «Ambito» to query the gestionale). */}
          <p className="mt-2.5 flex items-start gap-1.5 text-[11.5px] text-ink-3 leading-relaxed">
            <Database
              size={12}
              strokeWidth={2}
              className="mt-0.5 shrink-0 text-ando-orange"
              aria-hidden
            />
            <span>
              <Trans>
                Le domande con questo segno interrogano il gestionale. Apri
                «Ambito» per chiedere sui tuoi dati — fatture, clienti,
                fatturato.
              </Trans>
            </span>
          </p>
          <button
            type="button"
            onClick={() => useDemoTourStore.getState().start(onboardingKey())}
            className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-ando-orange transition-colors hover:text-ando-orange/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
          >
            <Compass size={13} strokeWidth={1.75} aria-hidden />
            <Trans>Rivedi il tour guidato</Trans>
          </button>
        </motion.section>
      ) : null}

      {/* ── Layer 1 — Greeting hero ──────────────────────────────────── */}
      <motion.section
        {...heroMotion}
        aria-label={i18n._(msg`Saluto e briefing`)}
        className="flex flex-col gap-3"
      >
        {/* Presiding brand anchor — centered and enlarged so it reads as the
            "mind" behind the briefing, echoing the login hero. The ``hero``
            variant leans toward the pointer, so it feels alive. */}
        <span aria-hidden className="block h-[88px] w-[88px] self-center">
          <LineBlob size="hero" strokeWidth={2} />
        </span>
        <h1 className="text-center font-semibold text-[28px] text-ink-1 leading-[1.15] tracking-tight">
          {greeting}
          {firstName ? ` ${firstName}` : ""}
        </h1>
        {showBullets ? (
          <p className="text-[14px] text-ink-2">
            {brief.isLoading ? (
              <Trans>Sto preparando il tuo briefing…</Trans>
            ) : bullets.length === 1 ? (
              <Trans>Ho una cosa interessante per te.</Trans>
            ) : (
              <Trans>Ho {bullets.length} cose interessanti per te.</Trans>
            )}
          </p>
        ) : null}

        {/* Bullets — aria-live so the screen reader announces them when
            the brief lands. Skeleton placeholder while loading. */}
        <ul aria-live="polite" aria-busy={brief.isLoading} className="flex flex-col gap-1.5">
          {brief.isLoading
            ? [0, 1, 2].map((i) => (
                <li
                  key={`skeleton-${i}`}
                  className="flex items-center gap-2 text-[13px] text-ink-2"
                >
                  <span aria-hidden className="h-1 w-1 shrink-0 rounded-full bg-ink-3/40" />
                  <span aria-hidden className="h-3 w-2/3 animate-pulse rounded-full bg-ink-3/15" />
                </li>
              ))
            : bullets.map((b, i) => (
                <li
                  key={`${b.text}-${i}`}
                  className="flex items-start gap-2 text-[13px] text-ink-2"
                >
                  <span
                    aria-hidden
                    className={cn(
                      "mt-[7px] h-1 w-1 shrink-0 rounded-full",
                      b.severity === "urgent"
                        ? "bg-ando-orange"
                        : b.severity === "attention"
                          ? "bg-amber-500"
                          : "bg-ink-3/50",
                    )}
                  />
                  <span className="min-w-0 flex-1">{b.text}</span>
                </li>
              ))}
        </ul>

        {bullets.length > 0 ? (
          <button
            type="button"
            onClick={handleHeroCta}
            // Static aria-label — destination is the Work hub
            // (multi-domain briefing) regardless of the brief's first
            // action. Reading the action label would mislead the SR
            // user about where the click actually lands (Jun 10 review).
            aria-label={i18n._(msg`Apri il briefing completo`)}
            className={cn(
              "mt-1 inline-flex items-center gap-1.5 self-start rounded-full",
              "border border-ando-orange/30 bg-ando-orange/10 px-3.5 py-1.5",
              "font-medium text-[12.5px] text-ando-orange",
              "transition-all hover:border-ando-orange/40 hover:bg-ando-orange/15",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            <span>
              <Trans>Vediamo tutto</Trans>
            </span>
            <ArrowRight size={13} strokeWidth={1.75} aria-hidden />
          </button>
        ) : null}
      </motion.section>

      {/* ── Layer 2 — Quick prompt chips contextual to active tab ────── */}
      <section
        aria-label={i18n._(msg`Domande veloci su ${tabLabel}`)}
        className="flex flex-col gap-2"
      >
        <div className="text-[13px] text-ink-3">
          <Trans>Domande veloci su {tabLabel}</Trans>
        </div>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {prompts.map((p, i) => {
            const Icon = p.icon;
            const label = i18n._(p.label);
            return (
              <motion.button
                key={p.id}
                type="button"
                onClick={() => handlePromptClick(p.prompt)}
                aria-label={label}
                {...chipMotion(i)}
                className={cn(
                  // Quiet suggestion — neutral hairline + ink text so it reads
                  // as a gentle hint, not a menu of buttons; orange accent
                  // surfaces only on hover.
                  "group inline-flex items-center gap-2 rounded-full",
                  "border border-hairline bg-transparent px-3 py-2",
                  "text-left font-medium text-[12.5px] text-ink-2",
                  "hover:-translate-y-[1px] transition-all hover:border-ando-orange/40 hover:bg-ando-orange/[0.05] hover:text-ando-orange",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                )}
              >
                <Icon
                  size={13}
                  strokeWidth={1.75}
                  aria-hidden
                  className="shrink-0 text-ando-orange/85 transition-colors group-hover:text-ando-orange"
                />
                <span className="min-w-0 flex-1 truncate">{label}</span>
              </motion.button>
            );
          })}
        </div>
      </section>

    </div>
  );
};

ChatEmptyState.displayName = "ChatEmptyState";
